import React, { useEffect, useState, useMemo } from 'react';
import { Box, Popper, Grow, MenuItem, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { efesOrange } from '../../../theme';

import useStyles from './makeStyles';

const NavBar = (props) => {
  const {
    routeTree,
    activeItem,
    setActiveItem,
    onSubClickHandler,
    selectedItem,
    currentPath,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSubMenuItem, setSelectedSubMenuItem] = useState({});
  const [subMenuItemAnchorEl, setSubMenuItemAnchorEl] = useState(null);
  const [firstLayerItemWidths, setFirstLayerItemWidths] = useState({});
  const [menuCloseTimeout, setMenuCloseTimeout] = useState(null);

  const activeItemChildren = useMemo(
    () => activeItem
      && activeItem.children
      && activeItem.children.filter((child) => !child.isAdminRoute),
    [activeItem],
  );

  useEffect(() => {
    if (!selectedItem) {
      setActiveItem(selectedItem);
    } else {
      setActiveItem(null);
      setAnchorEl(null);
      setSelectedSubMenuItem({});
      setSubMenuItemAnchorEl(null);
    }
  }, [selectedItem, setActiveItem]);

  useEffect(() => () => {
    clearTimeout(menuCloseTimeout);
  }, [menuCloseTimeout]);

  const handleFirstLayerHover = (e, item) => {
    setAnchorEl(null);
    setSelectedSubMenuItem({});
    setSubMenuItemAnchorEl(null);
    setFirstLayerItemWidths({});
    clearTimeout(menuCloseTimeout);

    const rect = e.currentTarget.getBoundingClientRect();
    const { width } = rect;

    setFirstLayerItemWidths((prevWidths) => ({
      ...prevWidths,
      [item.children[0].title]: width,
    }));

    setAnchorEl(e.currentTarget);
    setActiveItem(item);
    if (!item.children) {
      onSubClickHandler(item);
    }
  };

  const handleSecondLayerHover = (e, subMenuItem) => {
    setSelectedSubMenuItem(subMenuItem.children ? subMenuItem : {});
    clearTimeout(menuCloseTimeout);
    setSubMenuItemAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSubMenuItemAnchorEl(null);
    setActiveItem(null);
    setFirstLayerItemWidths({});
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      handleCloseMenu();
    }, 0);
    setMenuCloseTimeout(timeout);
  };

  return (
    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
      <div className={classes.wrapper}>
        {routeTree.map((item, index) => (
          <Button
            key={item.title}
            className={`${classes.buttonDiv} ${
              Boolean(anchorEl) && activeItem === item ? classes.customHover : ''
            }`}
            style={{
              width: index === routeTree.length - 1 ? 260 : '',
            }}
            onMouseOver={(e) => handleFirstLayerHover(e, item)}
            onMouseLeave={handleMouseLeave}
            aria-hidden="true"
            endIcon={(
              <ExpandLessIcon
                style={{
                  transform:
                    Boolean(anchorEl) && activeItem === item ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
                className={classes.expandIcon}
              />
            )}
          >
            {t(item.title)}
          </Button>
        ))}
      </div>
      <Popper
        className={classes.popper}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        transition
        placement="bottom-start"
        modifiers={{
          flip: {
            enabled: true,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'scrollParent',
          },
        }}
        onMouseLeave={handleMouseLeave}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <div className={classes.menuBox}>
              {activeItemChildren
                && activeItemChildren.map((subMenuItem) => {
                  const { title, path, children } = subMenuItem;
                  const isActive = currentPath.indexOf(path) > -1;
                  const isSelected = selectedSubMenuItem.title === title;
                  return (
                    <div key={subMenuItem.title}>
                      <MenuItem
                        onClick={() => {
                          if (!isActive && !children && path) {
                            onSubClickHandler(subMenuItem);
                          }
                        }}
                        onMouseEnter={(e) => handleSecondLayerHover(e, subMenuItem)}
                        onMouseOver={(e) => handleSecondLayerHover(e, subMenuItem)}
                        className={classes.menuItem}
                        style={{
                          fontWeight: isActive ? 'bold' : '',
                          color: isActive ? efesOrange : '',
                          minWidth: firstLayerItemWidths[subMenuItem.title],
                        }}
                      >
                        {t(title)}
                        {children && <PlayArrowIcon className={classes.childArrowIcon} />}
                      </MenuItem>
                      {isSelected && (
                        <Popper
                          open={Boolean(subMenuItemAnchorEl)}
                          anchorEl={subMenuItemAnchorEl}
                          transition
                          placement="right-start"
                          className={classes.popper}
                          modifiers={{
                            flip: {
                              enabled: true,
                            },
                            preventOverflow: {
                              enabled: true,
                              boundariesElement: 'scrollParent',
                            },
                          }}
                        >
                          {(popperProps) => (
                            <Grow {...popperProps.TransitionProps}>
                              <div className={cn(classes.subMenuBox, classes.menuBox)}>
                                {selectedSubMenuItem.children.map((childItem) => {
                                  const isSubMenuActive = currentPath.indexOf(childItem.path) > -1;
                                  return (
                                    <MenuItem
                                      key={childItem.title}
                                      onClick={() => {
                                        if (childItem.actionName) {
                                          dispatch(childItem.actionName());
                                        }
                                        onSubClickHandler(childItem);
                                        setSubMenuItemAnchorEl(null);
                                        setActiveItem(null);
                                        setAnchorEl(null);
                                        setSelectedSubMenuItem({});
                                      }}
                                      className={classes.menuItem}
                                      style={{
                                        fontWeight: isSubMenuActive ? 'bold' : '',
                                        color: isSubMenuActive ? efesOrange : '',
                                      }}
                                    >
                                      {t(childItem.title)}
                                    </MenuItem>
                                  );
                                })}
                              </div>
                            </Grow>
                          )}
                        </Popper>
                      )}
                    </div>
                  );
                })}
            </div>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

NavBar.propTypes = {
  routeTree: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      icon: PropTypes.element,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          path: PropTypes.string,
        }),
      ),
    }),
  ),
  activeItem: PropTypes.shape({
    header: PropTypes.string,
    icon: PropTypes.element,
    title: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        path: PropTypes.string,
      }),
    ),
  }),
  selectedItem: PropTypes.shape({
    header: PropTypes.string,
    icon: PropTypes.element,
    title: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        path: PropTypes.string,
      }),
    ),
  }),
  currentPath: PropTypes.string,
  setActiveItem: PropTypes.func,
  onSubClickHandler: PropTypes.func,
};

NavBar.defaultProps = {
  routeTree: [],
  activeItem: null,
  setActiveItem: () => {},
  onSubClickHandler: () => {},
  selectedItem: null,
  currentPath: '',
};

export default NavBar;
